// Секция действий цикла: сводка, детали, результаты, ручное управление
// днём; хостит под-вкладки переговоров и инициатив. Вынесено из
// CycleTab.tsx без изменения поведения (Фаза 1).

import { useState, useEffect } from "react";
import {
  api,
  type CampaignSetup,
  type CycleAction,
  type CycleActionType,
  type GameCycle,
} from "../api";
import {
  ACTION_GROUPS,
  ActionForm,
  ActionSituationModal,
  AfIcon,
  actionTypeLabel,
  type ActionGroupId,
  type ActionGroupOption,
} from "./ActionForm";
import { ActionInitiativesPanel } from "./Initiatives";
import { ActionNegotiationPanel } from "./NegotiationRooms";
import { consumePreferredActionWorkspace, defaultActionWorkspace, rememberPreferredActionWorkspace } from "./SituationSection";
import {
  CycleImages,
  DeadlineBadge,
  GAME_UI_ASSET_BASE,
  MockupSectionTitle,
  OwnerForcePhaseControl,
  SectionTitle,
  actionNarrativeStatusText,
  dayProgress,
  formatDateTime,
  type ActionWorkspaceTab,
  type Props,
  type Section,
} from "./shared";

function ActionSection(
  props: Props & { onNavigate?: (section: Section) => void },
) {
  const cycle = props.setup.cycle.current!;
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);
  const sortedActions = [...cycle.actions].sort((left, right) =>
    right.createdAt.localeCompare(left.createdAt),
  );
  const selectedAction = selectedActionId
    ? sortedActions.find((item) => item.id === selectedActionId)
    : undefined;
  const remainingActions = Math.max(0, cycle.actionLimit - cycle.actionsUsed);
  const situationText =
    cycle.centralProblem ||
    cycle.situationSummary ||
    props.setup.commandHub?.situation.shortText ||
    "Опишите ход, который поможет Совету повлиять на ситуацию.";
  const dayPct = dayProgress(cycle).percent;
  const [openSituation, setOpenSituation] = useState(false);
  const [workspace, setWorkspace] = useState<ActionWorkspaceTab>(() =>
    consumePreferredActionWorkspace(props.campaignId, cycle.id) ?? defaultActionWorkspace(cycle),
  );

  useEffect(() => {
    const preferred = consumePreferredActionWorkspace(props.campaignId, cycle.id);
    setWorkspace(preferred ?? defaultActionWorkspace(cycle));
  }, [props.campaignId, cycle.id, cycle.phase]);

  const switchWorkspace = (next: ActionWorkspaceTab) => {
    setWorkspace(next);
    rememberPreferredActionWorkspace(props.campaignId, cycle.id, next);
  };

  return (
    <div className="cc-af-room cc-cycle-section-stack cc-room-page cc-actions-room-page">
      <header className="cc-af-topbar">
        <span className="cc-af-brandicon"><AfIcon name="compass" /></span>
        <h1>Действия</h1>
        <span className="cc-af-remaining">Осталось <b>{remainingActions}/{cycle.actionLimit}</b></span>
      </header>

      <button type="button" className="cc-af-card cc-af-situation" onClick={() => setOpenSituation(true)}>
        <span className="cc-af-medallion">
          <img src={`${GAME_UI_ASSET_BASE}/icon_chronicle_situation.png`} alt="" width={46} height={46} />
        </span>
        <span className="cc-af-card-main">
          <small className="cc-af-eyebrow">Ситуация дня</small>
          <strong>{cycle.title || "Текущая проблема"}</strong>
          <p>{situationText}</p>
        </span>
      </button>

      <div className="cc-af-daybar">
        <DeadlineBadge cycle={cycle} />
        <div className="cc-af-prog" aria-hidden="true"><i style={{ width: `${dayPct}%` }} /></div>
        <b className="cc-af-pct">{dayPct}%</b>
      </div>

      <nav className="cc-af-subtabs" aria-label="Разделы действий">
        <button type="button" className={workspace === "actions" ? "on" : ""} onClick={() => switchWorkspace("actions")} aria-current={workspace === "actions" ? "page" : undefined}>Действия</button>
        <button type="button" className={workspace === "chats" ? "on" : ""} onClick={() => switchWorkspace("chats")} aria-current={workspace === "chats" ? "page" : undefined}>Чаты и встречи</button>
        <button type="button" className={workspace === "initiatives" ? "on" : ""} onClick={() => switchWorkspace("initiatives")} aria-current={workspace === "initiatives" ? "page" : undefined}>Инициативы</button>
      </nav>

      {workspace === "actions" && (
        <>
          {cycle.canAct ? (
            <ActionForm {...props} />
          ) : (
            <section className="cc-action-panel readonly cc-action-picker-panel cc-mockup-action-section">
              <MockupSectionTitle eyebrow="Действия" title="Действия недоступны" />
              <p>
                Новые действия сейчас недоступны, но последние ходы и подробности
                остаются открыты для проверки перед решениями Совета.
              </p>
            </section>
          )}
          <section className="cc-af-recent">
            <small className="cc-af-eyebrow">Последние действия</small>
            {sortedActions.length === 0 ? (
              <p className="cc-af-empty">
                В этом цикле ещё никто не выполнил действие.
              </p>
            ) : (
              <div className="cc-af-acts">
                {sortedActions.slice(0, 12).map((item) => (
                  <ActionSummaryCard
                    key={item.id}
                    action={item}
                    setup={props.setup}
                    onOpen={() => setSelectedActionId(item.id)}
                  />
                ))}
              </div>
            )}
          </section>
          <section className="cc-block cc-action-day-controls-card">
            <SectionTitle icon="⌛" eyebrow="Готовность" title="Завершение дня" />
            <p>
              Когда личные действия сделаны, можно завершить день для себя. Организатор
              может закрыть этап для всех в самом низу комнаты.
            </p>
            <ManualDayControls
              campaignId={props.campaignId}
              cycle={cycle}
              runAction={props.action}
            />
          </section>
        </>
      )}

      {workspace === "chats" && <ActionNegotiationPanel {...props} onNavigate={props.onNavigate} />}
      {workspace === "initiatives" && <ActionInitiativesPanel {...props} onNavigate={props.onNavigate} />}

      {openSituation && (
        <ActionSituationModal
          title={cycle.title || "Текущая проблема"}
          text={situationText}
          onClose={() => setOpenSituation(false)}
        />
      )}
      <OwnerForcePhaseControl {...props} />
      {selectedAction && (
        <ActionDetailsModal
          action={selectedAction}
          setup={props.setup}
          campaignId={props.campaignId}
          runAction={props.action}
          onNavigate={props.onNavigate}
          onClose={() => setSelectedActionId(null)}
        />
      )}
    </div>
  );
}
function actionGroupFallback(groupId: ActionGroupId): string {
  switch (groupId) {
    case "INTEL":
      return "⌕";
    case "DIPLOMACY":
      return "☷";
    case "COMMAND":
      return "⚔";
    case "FREE":
      return "◎";
  }
}
function actionGroupFor(actionType: CycleActionType): ActionGroupOption {
  return (
    ACTION_GROUPS.find((group) => group.subtypes.includes(actionType)) ??
    ACTION_GROUPS[ACTION_GROUPS.length - 1]
  );
}
function actionGroupIcon(actionType: CycleActionType): string {
  return actionGroupFor(actionType).icon;
}
function ActionSummaryCard({
  action,
  setup,
  onOpen,
}: {
  action: CycleAction;
  setup: CampaignSetup;
  onOpen: () => void;
}) {
  const narrativePending = action.narrativeStatus !== "READY";
  const summary = narrativePending
    ? action.description
    : action.deferredOutcomeText || action.achievementText || action.intentSummary || action.description;
  return (
    <button
      type="button"
      className={`cc-af-act ${action.visibility.toLowerCase()}`}
      onClick={onOpen}
    >
      <span className="cc-af-act-med" data-fallback={actionGroupFallback(actionGroupFor(action.actionType).id)}>
        <img
          src={`${GAME_UI_ASSET_BASE}/${actionGroupIcon(action.actionType)}`}
          alt=""
          width={36}
          height={36}
          onError={(event) => {
            event.currentTarget.style.display = "none";
            event.currentTarget.parentElement?.classList.add("missing");
          }}
        />
      </span>
      <span className="cc-af-act-copy">
        <span className="cc-af-act-top">
          <strong>{latestActionTitle(action)}</strong>
          {!narrativePending && (
            <span className={`cc-af-act-tag ${action.outcome.toLowerCase()}`}>{outcomeLabel(action.outcome)}</span>
          )}
        </span>
        <p>{summary}</p>
        <small>{action.actorDisplayName} · {formatDateTime(action.createdAt)}</small>
      </span>
    </button>
  );
}
function latestActionTitle(action: CycleAction): string {
  switch (action.actionType) {
    case "REQUEST_REPORT":
      return `${action.actorDisplayName} запросил сведения`;
    case "INVESTIGATE":
      return `${action.actorDisplayName} провёл расследование`;
    case "CHECK_RUMOR":
      return `${action.actorDisplayName} проверил слух`;
    case "MEET_FACTION":
    case "NEGOTIATE":
      return `${action.actorDisplayName} провёл переговоры`;
    case "PUBLIC_STATEMENT":
      return `${action.actorDisplayName} выступил с обращением`;
    case "PRESSURE_INSTITUTION":
      return `${action.actorDisplayName} поручил службе`;
    case "FORCE_OPERATION":
      return `${action.actorDisplayName} начал операцию`;
    case "MOBILIZE_RESOURCE":
      return `${action.actorDisplayName} организовал ресурс`;
    case "FREE_ACTION":
      return `${action.actorDisplayName} сделал свободный ход`;
    default:
      return `${action.actorDisplayName}: ${actionTypeLabel(action.actionType)}`;
  }
}
function ActionDetailsModal({
  action,
  setup,
  campaignId,
  runAction,
  onNavigate,
  onClose,
}: {
  action: CycleAction;
  setup: CampaignSetup;
  campaignId: string;
  runAction: Props["action"];
  onNavigate?: (section: Section) => void;
  onClose: () => void;
}) {
  return (
    <div className="cc-modal-backdrop cc-action-modal-backdrop" role="dialog" aria-modal="true">
      <div className="cc-modal-card cc-action-details-modal">
        <header className="cc-modal-header">
          <button type="button" className="cc-modal-close" onClick={onClose}>
            ×
          </button>
        </header>
        <ActionResultCard
          action={action}
          setup={setup}
          campaignId={campaignId}
          runAction={runAction}
          onNavigate={onNavigate}
        />
      </div>
    </div>
  );
}
function ActionResultCard({
  action,
  setup,
  campaignId,
  runAction,
  onNavigate,
}: {
  action: CycleAction;
  setup: CampaignSetup;
  campaignId: string;
  runAction: Props["action"];
  onNavigate?: (section: Section) => void;
}) {
  const narrativePending = action.narrativeStatus !== "READY";
  const character = setup.characters.find(
    (item) => item.userId === action.actorUserId,
  );
  const factors = [
    ...action.calculation.positiveFactors,
    ...action.calculation.negativeFactors,
  ].filter(
    (item) => item.code !== "UNCERTAINTY_CAP" && item.code !== "MINIMUM_CHANCE",
  );
  return (
    <article
      className={`cc-action-result-card ${action.visibility.toLowerCase()}`}
    >
      <header>
        <div>
          <span>
            {action.isMine
              ? "Ваше действие"
              : action.visibility === "PUBLIC"
                ? "Публичное действие"
                : "Личное действие"}
          </span>
          <h3>{action.actorDisplayName}</h3>
          <p>{character?.publicTitle ?? "Представитель Совета"}</p>
        </div>
        <span
          className={`cc-status-pill ${action.resolutionStatus === "PENDING_APPROVAL" ? "pending_approval" : action.resolutionStatus === "DECLINED" ? "declined" : narrativePending ? "narrative-pending" : action.resolutionStatus === "PENDING_DAY_END" ? "pending_day_end" : action.outcome.toLowerCase()}`}
        >
          {action.resolutionStatus === "PENDING_APPROVAL"
            ? "Ждёт согласования"
            : action.resolutionStatus === "DECLINED"
              ? "Отменено"
              : narrativePending
                ? "Формируется"
                : action.resolutionStatus === "PENDING_DAY_END"
                  ? "Ответ ожидается"
                  : outcomeLabel(action.outcome)}
        </span>
      </header>
      {action.participants.length > 1 && (
        <div className="cc-joint-action-panel">
          <strong>Совместное действие</strong>
          <div className="cc-joint-participants">
            {action.participants.map((participant) => (
              <span
                key={participant.userId}
                className={`cc-joint-status ${participant.status.toLowerCase()}`}
              >
                {participant.displayName}:{" "}
                {participant.initiator
                  ? "автор"
                  : participant.status === "APPROVED"
                    ? "согласовал"
                    : participant.status === "DECLINED"
                      ? "отклонил"
                      : "ожидает"}
              </span>
            ))}
          </div>
          {(action.canApprove || action.canDecline) && (
            <div className="cc-inline-actions">
              <button
                type="button"
                className="primary"
                onClick={() =>
                  void runAction("Подтверждаем совместное действие…", () =>
                    api.setJointActionApproval(campaignId, action.id, true),
                  )
                }
              >
                Согласовать участие
              </button>
              <button
                type="button"
                onClick={() =>
                  void runAction("Отклоняем совместное действие…", () =>
                    api.setJointActionApproval(campaignId, action.id, false),
                  )
                }
              >
                Отклонить
              </button>
            </div>
          )}
        </div>
      )}
      {!narrativePending && action.images.length > 0 && (
        <CycleImages
          campaignId={campaignId}
          assets={action.images}
          label="Иллюстрация действия"
          canManage={setup.canManage}
          action={runAction}
        />
      )}
      {!narrativePending && (
        <div className={`cc-action-guidance-band ${action.guidance.resultKind.toLowerCase()}`}>
          <span>{action.guidance.resultKindLabel}</span>
          <strong>{action.guidance.initiativePolicyText}</strong>
          <p>{action.guidance.whatNextText}</p>
        </div>
      )}
      <dl className="cc-action-result-summary">
        <div>
          <dt>Действие</dt>
          <dd>{actionContextLabel(action)}</dd>
        </div>
        <div>
          <dt>Суть</dt>
          <dd>
            {narrativePending
              ? action.description
              : action.intentSummary || action.description}
          </dd>
        </div>
        <div>
          <dt>Результат</dt>
          <dd>
            {narrativePending
              ? actionNarrativeStatusText(
                  action.narrativeStatus,
                  action.narrativeNextAttemptAt,
                )
              : action.resolutionStatus === "PENDING_DAY_END"
                ? action.achievementText
                : action.deferredOutcomeText || action.achievementText}
          </dd>
        </div>
      </dl>
      {!narrativePending &&
        action.knowledgeFindings &&
        action.knowledgeFindings.length > 0 && (
          <div className="cc-action-knowledge-findings">
            <h4>Открытые сведения</h4>
            {action.knowledgeFindings.map((fact) => (
              <article key={fact.id} className="cc-action-knowledge-fact">
                <b>{fact.title}</b>
                <p>{fact.concreteText}</p>
                {fact.evidenceText && (
                  <small>Основание: {fact.evidenceText}</small>
                )}
                {fact.actionHintText && (
                  <small>Как использовать: {fact.actionHintText}</small>
                )}
              </article>
            ))}
          </div>
        )}
      {!narrativePending && factors.length > 0 && (
        <details className="cc-action-influence">
          <summary>Что повлияло</summary>
          <div className="cc-action-factor-list">
            {factors.map((factor) => (
              <span
                className={factor.value >= 0 ? "positive" : "negative"}
                key={`${factor.code}-${factor.value}`}
              >
                <b>{shortFactorLabel(factor.code, factor.label)}</b>
                <i>{formatFactorValue(factor.code, factor.value)}</i>
              </span>
            ))}
          </div>
        </details>
      )}
      {!narrativePending && action.requiresInitiative && action.isMine && (
        <div className="cc-action-initiative-prompt">
          <strong>Требуется решение Совета</strong>
          <p>
            {action.guidance.initiativePolicyText || action.initiativeSuggestion?.description ||
              "Это не просто локальный итог: для устойчивого эффекта нужны ресурсы, исполнители или формальное решение Совета."}
          </p>
          <button
            type="button"
            className="primary"
            disabled={!setup.cycle.current?.canEditInitiative}
            onClick={() =>
              void runAction("Создаём инициативу из действия…", async () => {
                await api.createInitiativeFromAction(campaignId, action.id);
                rememberPreferredActionWorkspace(campaignId, setup.cycle.current!.id, "initiatives");
                onNavigate?.("actions");
              })
            }
          >
            {setup.cycle.current?.phase === "AGENDA"
              ? "Создать инициативу"
              : "Будет доступно на этапе инициатив"}
          </button>
        </div>
      )}
    </article>
  );
}
function ManualDayControls({
  campaignId,
  cycle,
  runAction,
}: {
  campaignId: string;
  cycle: GameCycle;
  runAction: Props["action"];
}) {
  if (cycle.phase !== "DAY") return null;
  if (cycle.myDayReady && cycle.actionsUsed < cycle.actionLimit)
    return (
      <button
        type="button"
        onClick={() =>
          void runAction("Возвращаем вас к действиям…", () =>
            api.setDayReadiness(campaignId, false),
          )
        }
      >
        Вернуться к действиям
      </button>
    );
  if (!cycle.myDayReady && cycle.canMarkDayReady)
    return (
      <button
        type="button"
        onClick={() =>
          void runAction("Завершаем ваш день…", () =>
            api.setDayReadiness(campaignId, true),
          )
        }
      >
        Завершить день
      </button>
    );
  return null;
}
function actionContextLabel(item: CycleAction): string {
  const target = item.targetDisplayName ? `«${item.targetDisplayName}»` : "";
  switch (item.actionType) {
    case "REQUEST_REPORT":
      return target
        ? `Официальный запрос к службе ${target}`
        : "Официальный запрос сведений";
    case "INVESTIGATE":
      return target
        ? `Расследование в службе ${target}`
        : "Расследование скрытых причин";
    case "CHECK_RUMOR":
      return "Проверка конкретного слуха";
    case "MEET_FACTION":
      return target ? `Встреча с фракцией ${target}` : "Встреча с фракцией";
    case "NEGOTIATE":
      return target
        ? `Переговоры с фракцией ${target}`
        : "Переговоры об условиях";
    case "PUBLIC_STATEMENT":
      return "Публичное обращение к жителям";
    case "PRESSURE_INSTITUTION":
      return target
        ? `Поручение службе ${target}`
        : "Поручение городской службе";
    case "MOBILIZE_RESOURCE":
      return target
        ? `Организация ресурса ${target}`
        : "Организация доступа к ресурсу";
    case "FORCE_OPERATION":
      return target ? `Силовая операция через ${target}` : "Силовая операция";
    case "FREE_ACTION":
      return "Свободное действие";
    case "PREPARE_INITIATIVE":
      return "Подготовка инициативы";
  }
}
function shortFactorLabel(code: string, label: string): string {
  if (code === "CHARACTER_INFLUENCE") return "Должность игрока";
  if (code === "FACTION_INFLUENCE") return "Влиятельность фракции";
  if (code === "FACTION_ATTITUDE") return "Отношение фракции";
  if (code === "INSTITUTION_LEVEL") return "Возможности службы";
  if (code === "INSTITUTION_LOAD") return "Загрузка службы";
  if (code === "RESOURCE_AVAILABILITY") return "Доступность ресурса";
  if (code === "APPROACH_SPECIFICITY") return "Конкретность подхода";
  if (code === "APPROACH_VAGUE") return "Недостаток конкретики";
  if (code === "JOINT_ACTION") return "Совместное действие";
  if (code === "JOINT_OFFICE_RELEVANCE") return "Должность соучастника";
  if (code.startsWith("INDICATOR_")) {
    const quoted = label.match(/«([^»]+)»/);
    return quoted?.[1] ?? "Состояние мира";
  }
  return label;
}
function formatFactorValue(_code: string, value: number): string {
  if (value === 0) return "0";
  const normalized =
    Math.sign(value) * Math.max(1, Math.round(Math.abs(value) / 5));
  return `${normalized > 0 ? "+" : ""}${normalized}`;
}
function outcomeLabel(value: string) {
  return value === "SUCCESS"
    ? "Успех"
    : value === "PARTIAL_SUCCESS"
      ? "Частичный успех"
      : "Неудача";
}

export {
  ActionSection,
  ActionSummaryCard,
  actionGroupFallback,
  actionGroupFor,
  actionGroupIcon,
  latestActionTitle,
  ActionDetailsModal,
  ActionResultCard,
  actionContextLabel,
  shortFactorLabel,
  formatFactorValue,
  outcomeLabel,
  ManualDayControls,
};
